import React from 'react';
import { useCurrency } from '../context/CurrencyContext';

/**
 * The blank price tag — the mark for a free listing.
 *
 * Purpose-drawn rather than pulled from lucide: the icon set's `Gift` reads as a
 * retail sale sticker, and at 11px its bow collapses into noise. This is a tag
 * with nothing written on it, which is literally what a free listing is. Four
 * straight edges and one filled eyelet, so it stays legible at badge size and
 * matches the 1px geometry the rest of the card is drawn in.
 */
function BlankTag({ size = 11.5 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="shrink-0"
    >
      <path d="M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0l-7.2-7.2A2 2 0 0 1 2.8 12V4.8A2 2 0 0 1 4.8 2.8H12a2 2 0 0 1 1.4.6l7.2 7.2a2 2 0 0 1 0 2.8Z" />
      <circle cx="7.6" cy="7.6" r="1.35" fill="currentColor" stroke="none" />
    </svg>
  );
}

/**
 * Renders a listing price. Free → an outlined, tag-marked "Free" chip; paid → the
 * price in the viewer's selected display currency (settlement is always USD).
 *
 * The chip is deliberately quiet. It shares its shape, hairline and accent ink
 * with the CLI chip beside it on the same card, so the price reads as one more
 * piece of listing metadata rather than a sale sticker stuck over the top. The
 * old filled gold pill also outshouted the paid price sitting in the next card,
 * which had the browse grid drawing the eye to the listings that earn nothing.
 *
 * `size` controls the free chip scale — `lg` is for the detail and showcase
 * pages, where the chip stands in for the hero price.
 */
export default function PriceTag({ usd, className = '', size = 'sm' }) {
  const { format } = useCurrency();
  const price = Number(usd) || 0;
  if (price <= 0) {
    const lg = size === 'lg';
    const dims = lg
      ? 'text-[0.78rem] px-3.5 py-1.5 gap-2'
      : 'text-[0.66rem] px-2.5 py-1 gap-1.5';
    return (
      <span
        className={`mono inline-flex items-center ${dims} rounded-full border border-[var(--accent)]/45 bg-[var(--accent)]/10 text-[var(--accent)] leading-none tracking-[0.16em] uppercase ${className}`}
      >
        <BlankTag size={lg ? 14 : 11.5} /> Free
      </span>
    );
  }
  return <span className={`mono text-[var(--text)] ${className}`}>{format(price)}</span>;
}
